// ─── Time windows on the planner calendar ────────────────────────────────────
//
// A booked window is an instant-to-instant range, but the planner calendar was
// built for date-only maintenance occurrences and forced every item to a single
// all-day cell. That understates a hold: a run of 30 hours appeared only on the
// day it started, so a machine looked free on the days it was actually taken.
//
// These helpers translate a window into what the calendar needs, and into the
// human duration shown on the pill. Pure and dependency-free so they can be
// unit-tested — vitest only picks up `src/**/*.test.ts` in the node env.

const MS_PER_MINUTE = 60_000
const MS_PER_HOUR = 3_600_000
const HOURS_PER_DAY = 24

/**
 * Local Y-M-D for an instant.
 *
 * Deliberately NOT `toISOString().slice(0, 10)`, which is UTC: the pill renders
 * its clock times with `toLocaleTimeString`, so placing the same event on a UTC
 * day puts an evening booking on the wrong cell relative to its own label.
 */
export function localDayIso(instant: Date | string): string {
  const d = instant instanceof Date ? instant : new Date(instant)
  if (Number.isNaN(d.getTime())) return ''
  const month = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

/** Whole days between two instants' local calendar days. 0 = same day. */
export function localDaySpan(startsAt: string, endsAt: string): number {
  const start = new Date(`${localDayIso(startsAt)}T00:00:00`)
  const end = new Date(`${localDayIso(endsAt)}T00:00:00`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / (HOURS_PER_DAY * MS_PER_HOUR)))
}

/**
 * Human duration for a pill: "45m", "4.5h", "2d 6h".
 *
 * Days are the unit that matters once a hold outlives a shift — "54h" makes a
 * reader do arithmetic to answer "is this machine free tomorrow".
 */
export function formatDuration(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return '0h'

  if (hours < 1) {
    return `${Math.round((hours * MS_PER_HOUR) / MS_PER_MINUTE)}m`
  }

  if (hours < HOURS_PER_DAY) {
    return `${trimZero(hours)}h`
  }

  const days = Math.floor(hours / HOURS_PER_DAY)
  const rest = hours - days * HOURS_PER_DAY
  // Sub-hour remainders are noise at this scale ("2d 0.3h" tells nobody
  // anything), so they round into the hour count.
  const restHours = Math.round(rest)
  if (restHours === 0) return `${days}d`
  if (restHours === HOURS_PER_DAY) return `${days + 1}d`
  return `${days}d ${restHours}h`
}

/**
 * Hours a window occupies on ONE local day.
 *
 * A card in a day cell must describe that day, not the whole booking: a run
 * from Monday 22:00 to Wednesday 02:00 takes 2h of Monday, all 24h of Tuesday
 * and 2h of Wednesday, and reporting "28h" in each cell would badly overstate
 * every one of them.
 */
export function reservationHoursOnDay(startsAt: string, endsAt: string, dayIso: string): number {
  const dayStart = new Date(`${dayIso}T00:00:00`)
  if (Number.isNaN(dayStart.getTime())) return 0
  const dayEnd = new Date(dayStart)
  dayEnd.setDate(dayEnd.getDate() + 1)

  const start = new Date(startsAt).getTime()
  const end = new Date(endsAt).getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0

  const overlap =
    Math.min(end, dayEnd.getTime()) - Math.max(start, dayStart.getTime())
  return overlap > 0 ? overlap / MS_PER_HOUR : 0
}

/** Clock time for a pill, in the reader's locale. */
export function clockTime(instant: string): string {
  return new Date(instant).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/** Short day+time, for windows that cross a day boundary. */
export function dayAndClock(instant: string): string {
  const d = new Date(instant)
  const day = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return `${day} ${clockTime(instant)}`
}

function trimZero(n: number): string {
  return `${Math.round(n * 10) / 10}`
}
